import { SwiperOptions } from 'swiper/types';
import { HoldService } from './../../../pages/hold/hold.service';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { PredefinedTemplatesService } from './../../../services/edit/predefined-templates.service';
import { SearchService } from '../../../services/search/search.service';
import { SharedService } from './../../../services/shared/shared.service';
import { StorageService } from './../../../services/storage/storage.service';
import { YourListService } from './../../../pages/your-list/your-list.service';
import { IShelfBrowseItem } from '../../../core/interfaces/shelf-browse.inteface';
import { AuthService, SYSTEMCATEGORY } from './../../../services/auth/auth.service';
import { finalize } from 'rxjs/operators';
import { RoutingService } from '../../../services/routing/routing.service';
import { Document } from '../../../core/interfaces/search.interface';
import { IListInfo, IListItem } from '../../../core/interfaces/list-interface';
import Swiper from 'swiper';

export interface IActiononOnShelfItem {
  action: string;
  item: IShelfBrowseItem;
}

export interface Showcase {
  autoplay: boolean;
  autoplaySpeed: number;
  backImg: string;
  background: string;
  className: string;
  color: string;
  flushed: boolean;
  infinite: boolean;
  items: ShowcaseItems[];
  listId: number;
  query: any;
  responsive: string;
  rowChips: [];
  slides: [];
  title: string;
  titleBackground: string;
  typeOf: string;
  variableWidth: string;
}
export interface ShowcaseItems {
  title: string;
  author: string;
  publisher: string;
  imageURL: string;
  agcontrolId: number;
  sourceLib: string;
}

@Component({
  selector: 'showcase',
  templateUrl: './slick-carousel.component.html',
  styleUrls: ['./slick-carousel.component.scss'],
  providers: [{ provide: ANIMATION_MODULE_TYPE, useValue: 'BrowserAnimations' }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SlickCarouselComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('swipe') swiperRef: ElementRef<HTMLElement>;
  @ViewChild('swipeNext') swiperNext: ElementRef<HTMLElement>;
  @ViewChild('swipePrev') swiperPrev: ElementRef<HTMLElement>;
  @Input() slides: any;
  @Input() slideCount: number;
  @Input() showcase: Showcase;
  @Input() dynamic: boolean;
  @Input() FRTargetType: string;
  @Output() actionEE = new EventEmitter<IActiononOnShelfItem>(null);

  private _holdService = inject(HoldService);
  private _predefinedTemplatesService = inject(PredefinedTemplatesService);
  private _route = inject(Router);
  private _routingService = inject(RoutingService);
  private _sharedService = inject(SharedService);
  private _storageService = inject(StorageService);
  private _yourListService = inject(YourListService);
  authService = inject(AuthService);
  searchService = inject(SearchService);

  private _searchResults = new BehaviorSubject<any>(null);
  searchResults$ = this._searchResults.asObservable();
  private _documents = new BehaviorSubject<Document[]>([]);
  documents$ = this._documents.asObservable();
  private _listItems = new BehaviorSubject<IListItem[]>([]);
  listItems$ = this._listItems.asObservable();
  private _message = new BehaviorSubject<string>(null);
  message$ = this._message.asObservable();

  searchRequest: any;
  listId: number;
  cid: string;
  lid: string;
  allowPatronGuestReserve: boolean;
  loading: boolean = true;

  SYSTEMCATEGORY = SYSTEMCATEGORY;
  systemCategory: number;

  private _swiper: Swiper;
  private _swiperConfig: SwiperOptions = {
    centeredSlides: false,
    direction: 'horizontal',
    keyboard: true,
    observer: true,
    pagination: false,
    rewind: true,
    a11y: {
      enabled: true,
    },
  };
  private _destroyed$ = new Subject<void>();

  ngOnInit() {
    this.systemCategory = this.authService.getUserDataFromSession().systemCategory;
    this.allowPatronGuestReserve = this._holdService.allowPatronGuestReserve();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this._swiper.destroy();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.shelf?.currentValue) {
      this._swiper?.autoplay.stop();
      this.loading = false;
    }

    if (changes.slideCount) {
      this._swiperConfig.breakpoints = this._setResponsive(changes.slideCount.currentValue);
    }

    if (changes.showcase) {
      if (changes.showcase.currentValue.autoplay) {
        this._swiper?.autoplay.start();
      } else {
        this._swiper?.autoplay.stop();
      }
    }

    if (this.dynamic) {
      this._dynamicShowcase();
    } else if (this.showcase) {
      this._listShowcase();
    }
  }

  ngAfterViewInit() {
    this._swiperConfig.navigation = {
      nextEl: this.swiperNext.nativeElement,
      prevEl: this.swiperPrev.nativeElement,
    };

    if (!this.showcase?.autoplay) {
      this._swiperConfig.autoplay = false;
    } else {
      this._swiperConfig.autoplay = {
        delay: 2000,
        stopOnLastSlide: false,
        pauseOnMouseEnter: true,
      };
    }

    this._swiperConfig.breakpoints = this._setResponsive(this.slideCount);
    this._swiper = new Swiper(this.swiperRef.nativeElement, this._swiperConfig);

    this.listItems$.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.showcase.autoplay ? this._swiper.autoplay.start() : this._swiper.autoplay.stop();
    });
    this.documents$.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this.showcase.autoplay ? this._swiper.autoplay.start() : this._swiper.autoplay.stop();
    });
  }

  private _setResponsive(slides) {
    // these breakpoints start from highest to smallest depending on columns. Starting Slides use MAX WIDTH NOT MIN WIDTH.
    switch (slides) {
      case 1:
      case 2:
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 1 },
          1200: { slidesPerView: 2 },
          1400: { slidesPerView: 2 },
          1700: { slidesPerView: 2 },
          2000: { slidesPerView: 2 },
        };
      case 3: // for columns 2-1-2 and 2-2-1. mobile breakpoint at 767.
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 2 },
          1200: { slidesPerView: 2 },
          1400: { slidesPerView: 3 },
          1700: { slidesPerView: 3 },
          2000: { slidesPerView: 3 },
        };
      case 4:
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 3 },
          1200: { slidesPerView: 3 },
          1400: { slidesPerView: 4 },
          1700: { slidesPerView: 4 },
          2000: { slidesPerView: 4 },
        };
      case 5:
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 3 },
          1200: { slidesPerView: 4 },
          1400: { slidesPerView: 5 },
          1700: { slidesPerView: 5 },
          2000: { slidesPerView: 5 },
        };
      case 6:
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 4 },
          1200: { slidesPerView: 5 },
          1400: { slidesPerView: 5 },
          1700: { slidesPerView: 6 },
          2000: { slidesPerView: 6 },
        };
      case 7: // for columns 2-1-2 and 2-2-1. mobile breakpoint at 767.
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 4 },
          1200: { slidesPerView: 4 },
          1400: { slidesPerView: 5 },
          1700: { slidesPerView: 6 },
          2000: { slidesPerView: 7 },
        };
      case 8: // for columns 2-1-3 and 2-3-1. mobile breakpoint at 767, also increased 950.
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 4 },
          1200: { slidesPerView: 5 },
          1400: { slidesPerView: 6 },
          1700: { slidesPerView: 7 },
          2000: { slidesPerView: 8 },
        };
      default:
        // 1 column settings. Does not respond to normal breakpoints.
        return {
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 4 },
          768: { slidesPerView: 5 },
          992: { slidesPerView: 6 },
          1200: { slidesPerView: 7 },
          1400: { slidesPerView: 8 },
          1700: { slidesPerView: 9 },
          2000: { slidesPerView: 10 },
        };
    }
  }

  action(action: string, item: IShelfBrowseItem): void {
    if (action) {
      this.actionEE.emit({ action: action, item: item });
    }
  }

  getShowcaseStyles() {
    const result = {
      color: this.showcase.color,
      'background-color': this.showcase.background,
    };
    return result;
  }

  goToFullRecord(item): void {
    if (this._route.url.includes('/page-widgets')) {
      this._routingService.goToFullRecordNewTab(item.agControlId, item.shard);
      return;
    }

    this.searchService.setSearchResults(this._searchResults.value);
    this._routingService.pushOnBackStack();
    this._routingService.goToFullRecord(
      item.agControlId,
      item.shard,
      this._searchResults.value.searchId,
      item.isRefdb,
      item.refdbFullRecUrl,
    );
  }

  goToFullRecordList(item) {
    this._routingService.goToFullRecordRelativeUrl(item.relativeUrl);
  }

  /**
   * List Showcase loads a showcase based on a listId
   */
  private _listShowcase() {
    this.loading = true;
    this.listId = this.showcase.listId;
    this.cid = this._storageService.getCid();
    this.lid = this._storageService.getLid();

    const payload = {
      listid: this.listId,
      cid: this.cid,
      lid: this.lid,
    };

    if (this.showcase.typeOf !== '') {
      const showcaseRequest =
        this.showcase.typeOf === 'popular-list'
          ? this._yourListService.getShowcasePopularList(this.listId)
          : this._yourListService.getShowcaseList(payload);

      showcaseRequest
        .pipe(
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe({
          next: (data) => {
            this._bindShowcaseInfo(data);
          },
          error: (error: unknown) => {
            this._sharedService.openSnackBar(`Unable to fetch ${this.showcase.typeOf}`);
            if (error instanceof Error) {
              console.error(error.message);
            }
          },
        });
    } else {
      this._sharedService.openSnackBar(`Unable to fetch ${this.showcase.typeOf}`);
      console.error('typeof showcase missing!!');
    }
  }

  private _bindShowcaseInfo(data: IListInfo) {
    this._listItems.next(data?.listItems ?? []);
    if (this._listItems.value.length > 0) {
      this._documents.next([]);
      this._message.next(null);
    } else if (this._listItems.value.length === 0) {
      this._message.next('The list is empty');
    }
  }

  /**
   * Dyanmic Showcase loads a showcase based on a search query
   */
  private _dynamicShowcase() {
    this.searchRequest = {
      searchQuery: {
        facetOffset: this.showcase.query.searchQuery.facetOffset ?? 0,
        filters: this.showcase.query.searchQuery.filters ?? {},
        matches: this.showcase.query.searchQuery.matches, // without this, showcase fails, so we need validation on EDIT-C-SHOWCASE
        numOfFacetPerGroup: this.showcase.query.searchQuery.numOfFacetPerGroup ?? 20,
        numOfRecords: 40,
        orderBy: this.showcase.query.searchQuery.orderBy ?? 'asc',
        resources:
          this.showcase.query.searchQuery.resources ?? this.searchService.getSearchResources(),
        resulttype: 'none', // one at a time, faster pull with not that much wait.
        sortBy: this.showcase.query.searchQuery.sortBy ?? ['relevance'],
        startRecord: this.showcase.query.searchQuery.startRecord ?? 1,
        isKidsSearch: this._sharedService.getIsKidsCat(),
      },
    };

    this.searchService.performSearchAndWait(this.searchRequest).subscribe((data) => {
      this._searchResults.next(data.results);
      this._documents.next(data.results.flatMap((r) => r.formats).flatMap((f) => f.documents));
      this._initShowCaseAutoplay();
    });
  }

  private _initShowCaseAutoplay(): void {
    setTimeout(() => {
      if (!this.showcase.autoplay) {
        this._swiperConfig.autoplay = false;
      }
    }, this._predefinedTemplatesService.SHOWCASE_TIMEOUT);
  }
}
